import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion } from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { api } from 'api'
import { CustomFieldRow } from './custom-field-row'
import {
  CustomFieldType,
  MappedCustomFieldType,
  SupportedCustomFieldConnectors,
} from './custom-field-type'
import { DataTypeSelection } from './data-type-selection'
import { DataType } from './data-types'
import { t } from '@lingui/core/macro'

const Container = styled.div`
  padding: 10px 0;
  margin: 20px 0;
`

const Header = styled.div`
  margin: 5px 0;
`

const Spacer = styled.div`
  margin: 10px 0;
`

const CustomFieldsContainer = styled.div`
  width: 550px;
`

const CustomFieldsDescription = styled.div`
  margin: 0 0 5px 0;
  width: 550px;
  display: flex;
  align-items: center;
`

const FieldDescription = styled.div`
  width: 240px;
  padding-left: 5px;
`

const DescriptionSpacer = styled.div`
  width: 15px;
`
interface ConnectorCustomFieldsProps {
  connectorId: number
  customFields: CustomFieldType[]
  updateCustomFields: (customFields: CustomFieldType[]) => void
  updateDeletedFields: (deletedFields: CustomFieldType) => void
  connectorType: SupportedCustomFieldConnectors
  usesDataType?: boolean
  dataTypeOptions?: DataType[]
}

export const ConnectorCustomFields = ({
  connectorId,
  customFields,
  updateCustomFields,
  updateDeletedFields,
  connectorType,
  usesDataType,
  dataTypeOptions,
}: ConnectorCustomFieldsProps) => {
  const [expanded, setExpanded] = useState(false)
  const [mappedCustomFields, setMappedCustomField] = useState<MappedCustomFieldType[]>([])
  const [selectedDataType, setSelectedDataType] = useState<DataType | undefined>(
    dataTypeOptions ? dataTypeOptions[0] : undefined,
  )

  useEffect(() => {
    api
      .get(`/api/v1/account/connector/${connectorId}/mapped-custom-fields`)
      .then(resp => {
        setMappedCustomField(resp.data)
      })
      .catch((err: unknown) => {
        console.log(err)
      })
  }, [connectorId])

  function addNewCustomField() {
    const emptyCustomField: CustomFieldType = {
      display_name: '',
      custom_field_name: '',
      required: false,
      connector_id: connectorId,
      data_type: selectedDataType ? selectedDataType.value : null,
      types: [],
    }
    const newFields = customFields.concat([emptyCustomField])
    updateCustomFields(newFields)
  }

  function handleCustomFieldChange(customField: CustomFieldType, index: number) {
    const updatedFields = customFields
    if (usesDataType && selectedDataType) {
      customField.data_type = selectedDataType.value
    }
    updatedFields[index] = customField
    updateCustomFields([...updatedFields])
  }

  function handleFieldDeletion(index: number) {
    const updatedFields = customFields.filter((_, i) => i !== index)
    updateCustomFields(updatedFields)

    const deletedField = customFields.find((_, i) => i == index)

    // Only add custom fields to be deleted to the connector payload if they have an id
    if (deletedField?.id) {
      updateDeletedFields(deletedField)
    }
  }

  function getCustomFieldDataType(customField: CustomFieldType): string {
    if (customField.data_type) return customField.data_type

    if (customField.types) {
      return customField.types[0]?.type || ''
    }

    return ''
  }

  function filterCustomFieldsByDataType(): CustomFieldType[] {
    if (!usesDataType || !selectedDataType) return customFields

    return customFields.filter(f => getCustomFieldDataType(f) === selectedDataType.value)
  }

  const filteredCustomFields = filterCustomFieldsByDataType()

  function customFieldIsMapped(customField: CustomFieldType): boolean {
    const mappedField = mappedCustomFields.find(m => m.integration_fields_id == customField.id)

    return !!mappedField
  }

  return (
    <Container>
      <Accordion
        expanded={expanded}
        onChange={() => {
          setExpanded(!expanded)
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Header>
            <Typography>{t`Custom field mappings`}</Typography>
            <Typography variant="caption">
              {t`Custom mappings allow you to connect Claravine integrations to fields unique to your system configuration.`}
            </Typography>
          </Header>
        </AccordionSummary>
        <AccordionDetails
          style={{
            marginTop: 0,
          }}
        >
          <CustomFieldsContainer>
            {usesDataType && dataTypeOptions && (
              <DataTypeSelection
                dataTypeOptions={dataTypeOptions}
                selected={selectedDataType?.value || ''}
                onChange={setSelectedDataType}
              />
            )}
            {!filteredCustomFields.length && (
              <Spacer>
                <Typography variant="caption">
                  {t`No field mappings currently setup. Click below to get started.`}
                </Typography>
              </Spacer>
            )}
            {customFields.map((cf, i) => (
              <CustomFieldRow
                customField={cf}
                index={i}
                handleChange={handleCustomFieldChange}
                key={i}
                handleDelete={handleFieldDeletion}
                allCustomFields={customFields}
                connectorType={connectorType}
                isMapped={customFieldIsMapped(cf)}
                isHidden={
                  usesDataType &&
                  selectedDataType &&
                  getCustomFieldDataType(cf) !== selectedDataType.value
                }
              />
            ))}
            {filteredCustomFields.length ? (
              <CustomFieldsDescription>
                <FieldDescription>
                  <Typography variant="caption">
                    {t`Used to identify the remote field in 3rd party system`}
                  </Typography>
                </FieldDescription>
                <DescriptionSpacer />
                <FieldDescription>
                  <Typography variant="caption">
                    User-friendly field name displayed when configuring integration mappings
                  </Typography>
                </FieldDescription>
              </CustomFieldsDescription>
            ) : (
              <div />
            )}
            <Button onClick={addNewCustomField} variant="outlined">
              {t`Add new`}
            </Button>
          </CustomFieldsContainer>
        </AccordionDetails>
      </Accordion>
    </Container>
  )
}
